// A dataset's column mapping, as edited in the web app and read to flag columns nobody has described. It is the
// `columns` list (plus timestamp and unmappedColumns) inside `Dataset.ingestMetadata`; datasetIngestFunction is
// the one validator (src/ingestSettings.js). See docs/plans/DATASET_COLUMN_MAPPING_PLAN.md.
//
// The editor works on the parsed JSON object, not a typed copy of it, so keys this file doesn't know about
// survive an edit.

/**
 * One entry of `ingestMetadata.columns`. A column entry has `source` (the header as sent) and `name` (what is
 * stored); a parameter entry has `parameter` instead and describes a whole forecast parameter.
 */
export interface ColumnMappingEntry {
  source?: string;
  name?: string;
  parameter?: string;
  label?: string;
  type?: string;
  unit?: string;
  description?: string;
}

export interface TimestampMapping {
  source?: string;
  timezone?: string;
  format?: string | string[];
}

export type UnmappedColumnsMode = "keep" | "drop" | "reject";

export interface ColumnMapping {
  timestamp?: TimestampMapping;
  columns?: ColumnMappingEntry[];
  unmappedColumns?: UnmappedColumnsMode;
  [key: string]: unknown;
}

/** What the file declared about one column (spec §3 per-parameter rows), kept on the upload by ingest. */
export interface DeclaredColumnInfo {
  unit?: string;
  name?: string;
  description?: string;
}

export interface DatasetWithColumns {
  id?: string;
  columns?: unknown;
  ingestMetadata?: unknown;
  datasetType?: string | null;
  parameterType?: string | null;
}

/** Common units offered beside free text. UDUNITS / CF style, as the spec asks (`ft3 s-1`, `degC`). */
export const COMMON_UNITS = [
  "cfs",
  "kcfs",
  "ft3 s-1",
  "m3 s-1",
  "acre-ft",
  "ft",
  "m",
  "mm",
  "in",
  "degC",
  "degF",
  "percent",
  "m s-1",
  "mph",
  "kPa",
  "hPa",
  "kW",
  "MW",
  "MWh",
  "GWh",
  "$/MWh",
  "USD"
];

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const keyOf = (value: unknown): string => String(value ?? "").trim().toLowerCase();

const cleanString = (value: unknown): string | undefined =>
  typeof value === "string" && value.trim() ? value.trim() : undefined;

/**
 * The spec's slug rule (§5.2): insert `_` at camelCase boundaries, replace every run of non-alphanumerics with
 * `_`, trim, lowercase. "Grand Coulee outflow" → "grand_coulee_outflow".
 */
export const slugifyColumnName = (text: string): string =>
  String(text ?? "")
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .replace(/[^A-Za-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .toLowerCase();

// ── Editing the mapping text ──────────────────────────────────────────────────────────────────────────────

/** Editor text → the mapping object. Blank is an empty mapping; anything that isn't a JSON object is an error. */
export const parseMappingText = (text: string): { mapping: ColumnMapping; error: string | null } => {
  if (!text.trim()) return { mapping: {}, error: null };
  try {
    const parsed = JSON.parse(text);
    if (!isPlainObject(parsed)) return { mapping: {}, error: "Ingest settings must be a JSON object: { … }" };
    return { mapping: parsed as ColumnMapping, error: null };
  } catch (err) {
    return { mapping: {}, error: `Not valid JSON: ${(err as Error).message}` };
  }
};

/** The mapping object → editor text. An empty mapping is blank text, which the form stores as null. */
export const serializeMapping = (mapping: ColumnMapping): string =>
  Object.keys(mapping).length === 0 ? "" : JSON.stringify(mapping, null, 2);

/**
 * The first thing in the mapping ingest would refuse, phrased for the editor, or null. Catches what the editor
 * itself can produce (a blank row, a repeated header or name); datasetIngestFunction stays the one validator.
 */
export const mappingProblem = (mapping: ColumnMapping): string | null => {
  const sources = new Set<string>();
  const names = new Set<string>();
  const parameters = new Set<string>();
  const entries = mappingEntries(mapping);
  // Ingest refuses every upload of a dataset that drops or rejects unlisted columns but lists none
  if ((mapping.unmappedColumns === "drop" || mapping.unmappedColumns === "reject") && !entries.some((e) => keyOf(e.source))) {
    return `"${mapping.unmappedColumns === "drop" ? "Drop" : "Reject"}" needs at least one column listed, or every upload would be refused`;
  }
  for (let i = 0; i < entries.length; i++) {
    const entry = entries[i];
    const row = `Column ${i + 1}`;
    if (entry.parameter !== undefined) {
      if (!keyOf(entry.parameter)) return `${row} needs a parameter name`;
      if (parameters.has(keyOf(entry.parameter))) return `${row}: parameter "${entry.parameter}" is described twice`;
      parameters.add(keyOf(entry.parameter));
      continue;
    }
    if (!keyOf(entry.source)) return `${row} needs the header it has in the file`;
    const name = keyOf(entry.name ?? entry.source);
    if (sources.has(keyOf(entry.source))) return `${row}: "${entry.source}" is mapped twice`;
    if (names.has(name)) return `${row}: "${entry.name ?? entry.source}" is used as a stored name twice`;
    sources.add(keyOf(entry.source));
    names.add(name);
  }
  return null;
};

export const mappingEntries = (mapping: ColumnMapping): ColumnMappingEntry[] =>
  Array.isArray(mapping.columns) ? mapping.columns.filter(isPlainObject) as ColumnMappingEntry[] : [];

const withEntries = (mapping: ColumnMapping, entries: ColumnMappingEntry[]): ColumnMapping => {
  const next: ColumnMapping = { ...mapping };
  if (entries.length === 0) delete next.columns;
  else next.columns = entries;
  return next;
};

/** Set fields on one entry; an emptied text field is removed rather than stored as "". */
export const updateMappingEntry = (mapping: ColumnMapping, index: number, patch: Partial<ColumnMappingEntry>): ColumnMapping => {
  const entries = mappingEntries(mapping).map((entry, i) => {
    if (i !== index) return entry;
    const next: Record<string, unknown> = { ...entry, ...patch };
    for (const key of Object.keys(patch)) {
      if (typeof next[key] === "string" && !(next[key] as string).trim()) delete next[key];
    }
    return next as ColumnMappingEntry;
  });
  return withEntries(mapping, entries);
};

export const addMappingEntries = (mapping: ColumnMapping, added: ColumnMappingEntry[]): ColumnMapping =>
  withEntries(mapping, [...mappingEntries(mapping), ...added]);

export const removeMappingEntry = (mapping: ColumnMapping, index: number): ColumnMapping =>
  withEntries(mapping, mappingEntries(mapping).filter((_, i) => i !== index));

export const setTimestampField = (mapping: ColumnMapping, field: keyof TimestampMapping, value: string): ColumnMapping => {
  const timestamp: Record<string, unknown> = { ...(isPlainObject(mapping.timestamp) ? mapping.timestamp : {}) };
  if (value.trim()) timestamp[field] = value;
  else delete timestamp[field];
  const next: ColumnMapping = { ...mapping };
  if (Object.keys(timestamp).length === 0) delete next.timestamp;
  else next.timestamp = timestamp as TimestampMapping;
  return next;
};

/** "keep" is the default, so choosing it removes the key rather than pinning a default in the stored JSON. */
export const setUnmappedColumnsMode = (mapping: ColumnMapping, mode: UnmappedColumnsMode): ColumnMapping => {
  const next: ColumnMapping = { ...mapping };
  if (mode === "keep") delete next.unmappedColumns;
  else next.unmappedColumns = mode;
  return next;
};

// ── Column kinds and parameters (spec §5) ─────────────────────────────────────────────────────────────────

export type ColumnKind =
  | "value"
  | "mean"
  | "percentile"
  | "member"
  | "control"
  | "obs"
  | "median"
  | "min"
  | "max"
  | "std"
  | "qc";

export interface ColumnKindInfo {
  kind: ColumnKind;
  /** The slug after the prefix is stripped; null for a legacy alias, which belongs to the file's one implicit parameter. */
  parameter: string | null;
}

const PREFIX_RULES: Array<[RegExp, ColumnKind]> = [
  [/^p_\d{2,3}_(.+)$/i, "percentile"],
  [/^m_\d{2,}_(.+)$/i, "member"],
  [/^mean_(.+)$/i, "mean"],
  [/^c_(.+)$/i, "control"],
  [/^obs_(.+)$/i, "obs"],
  [/^median_(.+)$/i, "median"],
  [/^min_(.+)$/i, "min"],
  [/^max_(.+)$/i, "max"],
  [/^std_(.+)$/i, "std"],
  // Quality flags for the parameter's values (bit codes, meanings in its `qc_codes` row); never a trace
  [/^qc_(.+)$/i, "qc"]
];

// Legacy aliases (spec §5.3): parsed, never emitted
const ALIAS_RULES: Array<[RegExp, ColumnKind]> = [
  [/^value$/i, "value"],
  [/^mean$/i, "mean"],
  [/^control$/i, "control"],
  [/^p_?\d{1,3}$/i, "percentile"],
  [/^m_?\d{1,3}$/i, "member"]
];

/** Which kind of series a column is and which parameter it is a series of. No recognised prefix = a `value` series. */
export const parseColumnKind = (name: string): ColumnKindInfo => {
  const text = String(name ?? "").trim();
  for (const [rule, kind] of PREFIX_RULES) {
    const match = rule.exec(text);
    if (match) return { kind, parameter: match[1] };
  }
  for (const [rule, kind] of ALIAS_RULES) {
    if (rule.test(text)) return { kind, parameter: null };
  }
  return { kind: "value", parameter: text };
};

// ── What has been seen, and what nobody has described ────────────────────────────────────────────────────

const isForecast = (dataset: DatasetWithColumns | null | undefined): boolean =>
  dataset?.datasetType === "FORECAST" || dataset?.datasetType === "ENSEMBLE_FORECAST";

/** Stored column names the dataset has held, without the time column. `Dataset.columns` is AWSJSON. */
export const observedColumns = (dataset: DatasetWithColumns | null | undefined): string[] => {
  let value: unknown = dataset?.columns;
  for (let i = 0; i < 2 && typeof value === "string"; i++) {
    try {
      value = JSON.parse(value);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(value)) return [];
  return value.filter((c): c is string => typeof c === "string" && c.trim() !== "" && keyOf(c) !== "timestamp");
};

/** The parameter a forecast's alias columns (`p05`, `m01`) belong to: named from the dataset record, else `value`. */
export const implicitParameter = (dataset: DatasetWithColumns | null | undefined): string =>
  slugifyColumnName(dataset?.parameterType || "") || "value";

// AWSJSON arrives as a string, sometimes stringified twice, as datasetIngestFunction also tolerates
const mappingOf = (dataset: DatasetWithColumns | null | undefined): ColumnMapping => {
  let value: unknown = dataset?.ingestMetadata;
  for (let i = 0; i < 2 && typeof value === "string"; i++) {
    try {
      value = JSON.parse(value);
    } catch {
      return {};
    }
  }
  return isPlainObject(value) ? (value as ColumnMapping) : {};
};

export interface UnmappedItem {
  /** The stored column name (historical) or the parameter (forecast) */
  key: string;
  kind: "column" | "parameter";
  /** The stored columns it stands for: one, or every p_NN_/m_NN_ column of the parameter */
  columns: string[];
}

/**
 * What the dataset has held that its mapping doesn't describe. Historical datasets are checked column by column:
 * a stored column is mapped when an entry's `name` is it. Forecast datasets are checked by parameter, so one
 * entry covers every percentile and member column of it. A dataset with no mapping has described nothing, so all
 * of its columns are unmapped; one that has not held a column yet has nothing to flag.
 */
export const getUnmappedItems = (dataset: DatasetWithColumns | null | undefined): UnmappedItem[] => {
  const columns = observedColumns(dataset);
  if (columns.length === 0) return [];

  const entries = mappingEntries(mappingOf(dataset));
  const names = new Set(entries.map((e) => keyOf(e.name ?? e.source)).filter(Boolean));
  const described = new Set(entries.map((e) => keyOf(e.parameter)).filter(Boolean));

  if (!isForecast(dataset)) {
    return columns.filter((c) => !names.has(keyOf(c))).map((c) => ({ key: c, kind: "column", columns: [c] }));
  }

  const implicit = implicitParameter(dataset);
  const groups = new Map<string, UnmappedItem>();
  for (const column of columns) {
    if (names.has(keyOf(column))) continue;
    const parameter = parseColumnKind(column).parameter ?? implicit;
    if (described.has(keyOf(parameter))) continue;
    const group = groups.get(keyOf(parameter));
    if (group) group.columns.push(column);
    else groups.set(keyOf(parameter), { key: parameter, kind: "parameter", columns: [column] });
  }
  return Array.from(groups.values());
};

/** The words for a flag: "2 columns not mapped", "1 parameter not labelled". Null when nothing is unmapped. */
export const describeUnmapped = (dataset: DatasetWithColumns | null | undefined): string | null => {
  const items = getUnmappedItems(dataset);
  if (items.length === 0) return null;
  if (isForecast(dataset)) return `${items.length} parameter${items.length === 1 ? "" : "s"} not labelled`;
  return `${items.length} column${items.length === 1 ? "" : "s"} not mapped`;
};

/** An entry's stored ID is fixed once the column holds data, whether it names the column or reads it: changing it
 * would send new uploads to a new column and leave the old rows behind. */
export const isEntryIdLocked = (dataset: DatasetWithColumns | null | undefined, entry: ColumnMappingEntry): boolean =>
  isColumnNameLocked(dataset, entry.name) || isColumnNameLocked(dataset, entry.source);

/**
 * The fields a label edit sets. The label's slug is offered as the stored name while the name is unset or still
 * following the label, until the column has data (the ID is then locked) or the name is typed by hand.
 */
export const labelEditPatch = (
  dataset: DatasetWithColumns | null | undefined,
  entry: ColumnMappingEntry,
  label: string
): Partial<ColumnMappingEntry> => {
  const patch: Partial<ColumnMappingEntry> = { label };
  if (entry.parameter === undefined && !isEntryIdLocked(dataset, entry)) {
    const followsLabel = !entry.name || entry.name === slugifyColumnName(entry.label || "");
    if (followsLabel) patch.name = slugifyColumnName(label);
  }
  return patch;
};

/** A stored column's `name` stays put once it has data: renaming would not rewrite what is already stored. */
export const isColumnNameLocked = (dataset: DatasetWithColumns | null | undefined, name: string | undefined): boolean =>
  Boolean(name) && observedColumns(dataset).some((c) => keyOf(c) === keyOf(name));

/** The entry Map creates for an unmapped item. A column keeps its stored name, so its history stays attached. */
export const entryForUnmapped = (item: UnmappedItem, declared?: DeclaredColumnInfo): ColumnMappingEntry => {
  const label = cleanString(declared?.name);
  const unit = cleanString(declared?.unit);
  const description = cleanString(declared?.description);
  const described = { ...(label ? { label } : {}), ...(unit ? { unit } : {}), ...(description ? { description } : {}) };
  return item.kind === "parameter" ? { parameter: item.key, ...described } : { source: item.key, name: item.key, ...described };
};

/**
 * Column metadata from uploads (newest first), merged so the newest declaration of each column wins. Used to
 * prefill Map: the dataset's own mapping still wins once saved.
 */
export const declaredColumnInfoFrom = (uploads: Array<{ columnMetadata?: unknown }>): Record<string, DeclaredColumnInfo> => {
  const merged: Record<string, DeclaredColumnInfo> = {};
  for (const upload of uploads) {
    let value = upload.columnMetadata;
    if (typeof value === "string") {
      try {
        value = JSON.parse(value);
      } catch {
        continue;
      }
    }
    if (!isPlainObject(value)) continue;
    for (const [column, info] of Object.entries(value)) {
      if (!isPlainObject(info)) continue;
      const key = keyOf(column);
      merged[key] = { ...(info as DeclaredColumnInfo), ...merged[key] };
    }
  }
  return merged;
};

export const declaredInfoFor = (declared: Record<string, DeclaredColumnInfo>, item: UnmappedItem): DeclaredColumnInfo | undefined =>
  declared[keyOf(item.key)];
